import { Inject, Injectable, makeStateKey, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DataService } from './data/data.service';
import { GetCollectionPageQuery, GetCollectionPageQueryVariables } from '../../common/generated-types';
import { GET_COLLECTION_PAGE } from '../../common/graphql/documents.graphql';
import { NGXLogger } from 'ngx-logger';
import { StateService } from './state/state.service';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

const COLLECTION_RESOLVER_STORE_IDENTIFIER_KEY = makeStateKey<string>('PAGE_RESOLVER_STORE_IDENTIFIER');

@Injectable({ providedIn: 'root' })
export class CollectionResolver implements Resolve<any> {
  collectionSlug: string;
  storeIdentifier: string;
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject('STORE_IDENTIFIER') public storeIdentifierFromSSR: string | null,
    private dataService: DataService,
    private stateService: StateService,
    private transferState: TransferState,
    private logger: NGXLogger,
    private router: Router
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    this.collectionSlug = route.paramMap.get('slug')?.toLowerCase() || '';
    if (isPlatformServer(this.platformId)) {
      this.storeIdentifier = this.storeIdentifierFromSSR || '';
      this.transferState.set(COLLECTION_RESOLVER_STORE_IDENTIFIER_KEY, this.storeIdentifier);
    }
    return this.stateService.select(state => state.storeIdentifier).pipe(
      switchMap(storeIdentifier => {
        if (isPlatformBrowser(this.platformId)) {
          if(!storeIdentifier) {
            this.storeIdentifier = this.transferState.get(COLLECTION_RESOLVER_STORE_IDENTIFIER_KEY, '');
            this.transferState.remove(COLLECTION_RESOLVER_STORE_IDENTIFIER_KEY);
          } else {
            this.storeIdentifier = storeIdentifier;
          }
        }
        this.logger.debug(`CollectionResolver for ${this.collectionSlug} ${storeIdentifier}`);
        return this.dataService.query<GetCollectionPageQuery, GetCollectionPageQueryVariables>(GET_COLLECTION_PAGE, {
          identifier: this.storeIdentifier,
          slug: this.collectionSlug
        }).pipe(
          map(result => {
            this.logger.debug(`CollectionResolver: result ${result}`);
            if (result?.collectionPage && result?.collectionPage?.collectionInfo !== null) {
              return result?.collectionPage;
            } else {
              this.logger.error(`CollectionResolver: Collection Page not found for slug '${this.collectionSlug}'`);
              this.router.navigateByUrl('/not-found', { skipLocationChange: true });
              return null;
            }
          }),
          catchError((err) => {
            // In case of errors, also go to not-found
            this.logger.error(`CollectionResolver: Error ${err} fetching product page for slug '${this.collectionSlug}'`);
            this.router.navigateByUrl('/not-found', { skipLocationChange: true });
            return of(null);
          })
        )
      })
    );
  }
}
